import { Link, Typography } from '@material-ui/core';
import { CheckboxField, Inline, Stack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useState } from 'react';
import { useHref, useLocation } from 'react-router-dom';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import croppedThunder from 'shared/modules/superpay/assets/cropped_thunder.png';
import {
  SUPERPAY_PROFILE_ROUTES,
  SUPERPAY_WEBVIEW_ROUTES,
} from 'carrier/profile/superpay/SuperPayRoute';
import { InstantTransferFormDescription } from 'shared/modules/superpay/instant-transfer/InstantTransferFormDescription';
import styled from 'styled-components';
import { SuperPayInstantTransferDialogDTO } from '../SuperPayDTO';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';

const Background = styled(Box)`
  border-radius: 0 0 30px 0;
`;

const Body = styled(Box)`
  z-index: 1;
  flex-direction: column;
  gap: 24px;
`;

function InstantTransferFormBackground() {
  return (
    <Background
      backgroundColor="Silver200"
      position="absolute"
      top={0}
      height={['190px', '170px', '180px']}
      width="100%"
      display="flex"
      justifyContent="end"
    >
      <Box width={['110px', '115px']}>
        <img src={croppedThunder} width="100%" alt="thunder illustration" />
      </Box>
    </Background>
  );
}

interface InstantTransferDialogContentProps {
  onClose: () => void;
  dialogInformation: SuperPayInstantTransferDialogDTO;
  onConfirmInstantTransfer: () => void;
}

export function InstantTransferForm({
  dialogInformation,
  onClose,
  onConfirmInstantTransfer,
}: InstantTransferDialogContentProps) {
  const [isTermsAgree, setTermsAgree] = useState(false);
  const location = useLocation();

  const formattedNewPrice = formatCurrency(dialogInformation.new_price, {
    minimumFractionDigits: 0,
  });

  const termsPath = useHref(
    isWebView
      ? SUPERPAY_WEBVIEW_ROUTES.instantTransferTerms
      : SUPERPAY_PROFILE_ROUTES.instantTransferTerms,
  );

  const termsHref = isWebView
    ? `${termsPath}?${new URLSearchParams({
        next: `${location.pathname}${location.search}`,
      }).toString()}`
    : termsPath;

  return (
    <Box position="relative" height="100%">
      <InstantTransferFormBackground />
      <Body
        padding="medium"
        position="relative"
        height="100%"
        display="flex"
        justifyContent="space-between"
      >
        <InstantTransferFormDescription dialogInformation={dialogInformation} />
        <Stack space="small">
          <CheckboxField
            checked={isTermsAgree}
            onChange={(_, value) => {
              setTermsAgree(value);
            }}
            label={
              <Typography>
                I accept the{' '}
                <Link
                  component="a"
                  href={termsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  color="primary"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    openExternalURL(termsHref, { target: '_blank' });
                  }}
                >
                  Instant Transfer Terms and Conditions
                </Link>
              </Typography>
            }
          />
          {isWebView ? (
            <PermissionButton
              name="instant_transfer"
              permission="can_edit"
              fullWidth={true}
              disabled={!isTermsAgree}
              onClick={onConfirmInstantTransfer}
            >
              Get {formattedNewPrice} Now
            </PermissionButton>
          ) : (
            <Inline horizontalAlign="right">
              <Button variant="text" onClick={onClose}>
                No, thank you
              </Button>
              <PermissionButton
                name="instant_transfer"
                permission="can_edit"
                disabled={!isTermsAgree}
                onClick={onConfirmInstantTransfer}
              >
                Get {formattedNewPrice} Now
              </PermissionButton>
            </Inline>
          )}
        </Stack>
      </Body>
    </Box>
  );
}
